import { useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type ApiClient, createApiClient } from "@/api/client";
import { useAuthSettings } from "@/auth/authSettingsContext";

export function useApi(): ApiClient {
  const { instance } = useMsal();
  const { apiScope, apiBaseUrl } = useAuthSettings();
  return useMemo(
    () => createApiClient(instance, apiScope, apiBaseUrl),
    [instance, apiScope, apiBaseUrl],
  );
}

export interface AsyncState<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  reload: () => void;
}

export function useApiQuery<T>(path: string | null): AsyncState<T> {
  const api = useApi();
  const [nonce, setNonce] = useState(0);
  const [settled, setSettled] = useState<{
    key: string;
    path: string;
    data?: T;
    error?: Error;
  }>();
  const key = path === null ? null : `${nonce}:${path}`;
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (key === null || path === null) return;
    let cancelled = false;
    api
      .get<T>(path)
      .then((data) => {
        if (!cancelled) setSettled({ key, path, data });
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setSettled({
            key,
            path,
            error: caught instanceof Error ? caught : new Error(String(caught)),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, path, key]);

  const current = settled?.key === key ? settled : undefined;
  // While a reload of the same path is in flight, keep showing what was there rather than blanking.
  const previous = settled?.path === path ? settled : undefined;
  return {
    data: current ? current.data : previous?.data,
    error: current?.error,
    loading: key !== null && current === undefined,
    reload,
  };
}
